'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useStore } from 'jotai'

import { useAuth } from '@/features/auth/hooks'
import { markRfqCancelled } from '@/features/rfq/lib/rfq-store'
import {
  executeAlgo,
  startAdvancedTwapAlgo,
  startHedgeAlgo,
  startLimitChaseAlgo,
  startLimitChaseAlgoBatch,
  startScaleAlgo,
  startSingleHedgeAlgo,
  startSpreaderAlgo,
  startTwapAlgo,
  startTwapAlgoBatch,
  stopAlgo,
  type WithCorrelation,
} from '@/service/algo.api'
import { reconcileTerminalAlgoState } from '@/service/ws/account-order-merge'
import { algoStatesRawAtom } from '@/state/account'
import { rfqsRawAtom } from '@/state/rfq'
import {
  AdvancedTwapAlgoParams,
  AlgoServiceCommand,
  AlgoStatus,
  HedgeAlgoParams,
  LimitChaseAlgoParams,
  ScaleAlgoParams,
  SingleHedgeAlgoParams,
  SpreaderAlgoParams,
  StopAlgo,
  TwapAlgoParams,
} from '@/types/generated/algo'
import { Success } from '@/types/generated/public_api'
import { ErrorResponse } from '@/types/service'

export const algoKeys = {
  all: (userId?: string | null) => ['algo', userId] as const,
  strategies: (userId?: string | null) => [...algoKeys.all(userId), 'strategies'] as const,
}

export const useExecuteAlgo = (props?: {
  onSuccess?: (data: Success, variables: AlgoServiceCommand) => void
  onError?: (error: ErrorResponse, variables: AlgoServiceCommand) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: AlgoServiceCommand) => executeAlgo(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartTwapAlgo = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<TwapAlgoParams>) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<TwapAlgoParams>) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<TwapAlgoParams>) => startTwapAlgo(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartTwapAlgoBatch = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<TwapAlgoParams>[]) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<TwapAlgoParams>[]) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payloads: WithCorrelation<TwapAlgoParams>[]) => startTwapAlgoBatch(payloads),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartSpreaderAlgo = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<SpreaderAlgoParams>) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<SpreaderAlgoParams>) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<SpreaderAlgoParams>) => startSpreaderAlgo(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartAdvancedTwapAlgo = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<AdvancedTwapAlgoParams>) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<AdvancedTwapAlgoParams>) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<AdvancedTwapAlgoParams>) =>
      startAdvancedTwapAlgo(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartScaleAlgo = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<ScaleAlgoParams>) => startScaleAlgo(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
    },
  })
}

export const useStartSingleHedgeAlgo = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<SingleHedgeAlgoParams>) => startSingleHedgeAlgo(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
    },
  })
}

export const useStartLimitChaseAlgo = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<LimitChaseAlgoParams>) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<LimitChaseAlgoParams>) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<LimitChaseAlgoParams>) => startLimitChaseAlgo(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartLimitChaseAlgoBatch = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<LimitChaseAlgoParams>[]) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<LimitChaseAlgoParams>[]) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payloads: WithCorrelation<LimitChaseAlgoParams>[]) =>
      startLimitChaseAlgoBatch(payloads),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStartHedgeAlgo = (props?: {
  onSuccess?: (data: Success, variables: WithCorrelation<HedgeAlgoParams>) => void
  onError?: (error: ErrorResponse, variables: WithCorrelation<HedgeAlgoParams>) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: WithCorrelation<HedgeAlgoParams>) => startHedgeAlgo(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useStopAlgo = (props?: {
  onSuccess?: (data: Success, variables: StopAlgo) => void
  onError?: (error: ErrorResponse, variables: StopAlgo) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const store = useStore()
  return useMutation({
    mutationFn: (payload: StopAlgo) => stopAlgo(payload),
    onSuccess: (data, variables) => {
      const states = store.get(algoStatesRawAtom)
      const stopped = states.find(
        (state) =>
          state.id === variables.algoId &&
          (state.accountIds.length === 0 || state.accountIds.includes(variables.accountId))
      )
      store.set(algoStatesRawAtom, (prev) =>
        reconcileTerminalAlgoState(prev, variables, AlgoStatus.ALGO_STATUS_CANCELLED)
      )
      const currentRfqId = stopped?.algoData?.rfq?.currentRfqId
      if (currentRfqId) {
        store.set(rfqsRawAtom, (prev) =>
          markRfqCancelled(prev, { accountId: variables.accountId, rfqId: currentRfqId })
        )
      }
      queryClient.invalidateQueries({ queryKey: algoKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}
